import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, KeyRound, ArrowLeft, Send } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Handle forgot password logic here
    setEmail(trimmedEmail);
    setSubmitted(true);
  };

  return (
    <>
      <Header />
      <main className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden font-sans px-6 py-16">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>

        <div className="absolute top-[-12%] left-[-8%] w-[460px] h-[460px] bg-[#f59e0b]/10 rounded-full blur-[120px] pointer-events-none"></div>

        <section className="relative z-10 w-full max-w-md">
          <div className="bg-white border border-zinc-100 rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.05)] p-8 md:p-10">
            <div className="flex flex-col items-center mb-8">
              <div className="w-14 h-14  rounded-2xl flex items-center justify-center mb-4 border border-[#f59e0b]">
                <KeyRound className="w-7 h-7 text-[#f59e0b]" />
              </div>
              <h1 className="text-3xl font-black text-zinc-950 tracking-tighter">Forgot Password</h1>
              <p className="text-[zinc-500] text-sm font-medium mt-1 text-center">
                Enter your email to receive a reset link.
              </p>
            </div>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {error && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                    {error}
                  </div>
                )}
                <div className="relative group">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1 block">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-amber-600 transition-colors" />
                    <input
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-400/5 transition-all"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="mt-2 bg-zinc-950 text-white font-black py-4 rounded-2xl hover:bg-[#f59e0b] hover:text-zinc-950 transition-all shadow-xl shadow-zinc-200 flex items-center justify-center gap-2 uppercase tracking-widest text-xs"
                >
                  Send Reset Link <Send className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <div className="rounded-2xl bg-amber-50 border border-amber-100 px-6 py-5 text-center">
                <p className="text-zinc-700 text-sm font-semibold leading-relaxed">
                  Reset instructions were sent to <span className="text-zinc-950">{email}</span>.
                </p>
              </div>
            )}

            <div className="mt-8 text-center border-t border-zinc-50 pt-8">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-950 font-black text-xs uppercase tracking-widest transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back To Login
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ForgotPassword;
